import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

const errMsg = (e, fallback) =>
  e.response?.data?.message || e.message || fallback;

// ------------------ DISTRICT ------------------
export const fetchDistrictListThunk = createAsyncThunk(
  "dtMaster/fetchDistricts",
  async (q = "", { rejectWithValue }) => {
    try {
      const res = await api.get("/api/taluk-district/districts", { params: { q } });
      return res.data?.districts || [];
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to load districts"));
    }
  }
);

export const createDistrictThunk = createAsyncThunk(
  "dtMaster/createDistrict",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.post("/api/taluk-district/districts", payload);
      if (!res.data?.success) throw new Error(res.data?.message || "Create failed");
      dispatch(fetchDistrictListThunk());
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to create district"));
    }
  }
);

export const updateDistrictThunk = createAsyncThunk(
  "dtMaster/updateDistrict",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const dist_id = Number(payload?.dist_id || 0);
      if (!dist_id) throw new Error("dist_id missing");
      const res = await api.put(`/api/taluk-district/districts/${dist_id}`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || "Update failed");
      dispatch(fetchDistrictListThunk());
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to update district"));
    }
  }
);

export const deleteDistrictThunk = createAsyncThunk(
  "dtMaster/deleteDistrict",
  async (dist_id, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.delete(`/api/taluk-district/districts/${dist_id}`);
      if (!res.data?.success) throw new Error(res.data?.message || "Delete failed");
      dispatch(fetchDistrictListThunk());
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to delete district"));
    }
  }
);

// ------------------ TALUK ------------------
export const fetchTalukListThunk = createAsyncThunk(
  "dtMaster/fetchTaluks",
  async ({ dist_id = "", q = "" } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/taluk-district/taluks", { params: { dist_id, q } });
      return res.data?.taluks || [];
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to load taluks"));
    }
  }
);

export const createTalukThunk = createAsyncThunk(
  "dtMaster/createTaluk",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.post("/api/taluk-district/taluks", payload);
      if (!res.data?.success) throw new Error(res.data?.message || "Create failed");

      // refresh based on selected filter
      dispatch(fetchTalukListThunk({ dist_id: payload?.dist_id || "" }));
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to create taluk"));
    }
  }
);

export const updateTalukThunk = createAsyncThunk(
  "dtMaster/updateTaluk",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const taluk_id = Number(payload?.taluk_id || 0);
      if (!taluk_id) throw new Error("taluk_id missing");

      const res = await api.put(`/api/taluk-district/taluks/${taluk_id}`, payload);
      if (!res.data?.success) throw new Error(res.data?.message || "Update failed");

      dispatch(fetchTalukListThunk({ dist_id: payload?.dist_id || "" }));
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to update taluk"));
    }
  }
);

export const deleteTalukThunk = createAsyncThunk(
  "dtMaster/deleteTaluk",
  async ({ taluk_id, dist_id }, { dispatch, rejectWithValue }) => {
    try {
      const res = await api.delete(`/api/taluk-district/taluks/${taluk_id}`);
      if (!res.data?.success) throw new Error(res.data?.message || "Delete failed");

      dispatch(fetchTalukListThunk({ dist_id: dist_id || "" }));
      return true;
    } catch (e) {
      return rejectWithValue(errMsg(e, "Failed to delete taluk"));
    }
  }
);

const slice = createSlice({
  name: "districtTalukMaster",
  initialState: {
    // district
    districts: [],
    districtLoading: false,
    districtSaving: false,
    districtDeleting: false,

    // taluk
    taluks: [],
    talukLoading: false,
    talukSaving: false,
    talukDeleting: false,

    // filters
    talukFilters: { dist_id: "", q: "" },

    // modal
    modalOpen: false,
    modalMode: "add", // add/edit
    modalType: "district", // district/taluk

    form: {
      dist_id: "",
      dist_name: "",
      taluk_id: "",
      taluk_name: "",
      status: 1,
    },

    error: null,
  },
  reducers: {
    openDtModal(state, action) {
      state.modalOpen = true;
      state.modalMode = action.payload?.mode || "add";
      state.modalType = action.payload?.type || "district";
      state.error = null;
    },
    closeDtModal(state) {
      state.modalOpen = false;
      state.error = null;
    },
    setDtForm(state, action) {
      state.form = { ...state.form, ...(action.payload || {}) };
    },
    resetDtForm(state) {
      state.form = {
        dist_id: "",
        dist_name: "",
        taluk_id: "",
        taluk_name: "",
        status: 1,
      };
    },
    setTalukFilters(state, action) {
      state.talukFilters = { ...state.talukFilters, ...(action.payload || {}) };
    },
  },
  extraReducers: (b) => {
    // districts
    b.addCase(fetchDistrictListThunk.pending, (s) => {
      s.districtLoading = true;
      s.error = null;
    });
    b.addCase(fetchDistrictListThunk.fulfilled, (s, a) => {
      s.districtLoading = false;
      s.districts = a.payload || [];
    });
    b.addCase(fetchDistrictListThunk.rejected, (s, a) => {
      s.districtLoading = false;
      s.districts = [];
      s.error = a.payload || "Failed to load districts";
    });

    b.addCase(createDistrictThunk.pending, (s) => {
      s.districtSaving = true;
      s.error = null;
    });
    b.addCase(createDistrictThunk.fulfilled, (s) => {
      s.districtSaving = false;
    });
    b.addCase(createDistrictThunk.rejected, (s, a) => {
      s.districtSaving = false;
      s.error = a.payload || "Create failed";
    });

    b.addCase(updateDistrictThunk.pending, (s) => {
      s.districtSaving = true;
      s.error = null;
    });
    b.addCase(updateDistrictThunk.fulfilled, (s) => {
      s.districtSaving = false;
    });
    b.addCase(updateDistrictThunk.rejected, (s, a) => {
      s.districtSaving = false;
      s.error = a.payload || "Update failed";
    });

    b.addCase(deleteDistrictThunk.pending, (s) => {
      s.districtDeleting = true;
      s.error = null;
    });
    b.addCase(deleteDistrictThunk.fulfilled, (s) => {
      s.districtDeleting = false;
    });
    b.addCase(deleteDistrictThunk.rejected, (s, a) => {
      s.districtDeleting = false;
      s.error = a.payload || "Delete failed";
    });

    // taluks
    b.addCase(fetchTalukListThunk.pending, (s) => {
      s.talukLoading = true;
      s.error = null;
    });
    b.addCase(fetchTalukListThunk.fulfilled, (s, a) => {
      s.talukLoading = false;
      s.taluks = a.payload || [];
    });
    b.addCase(fetchTalukListThunk.rejected, (s, a) => {
      s.talukLoading = false;
      s.taluks = [];
      s.error = a.payload || "Failed to load taluks";
    });

    b.addCase(createTalukThunk.pending, (s) => {
      s.talukSaving = true;
      s.error = null;
    });
    b.addCase(createTalukThunk.fulfilled, (s) => {
      s.talukSaving = false;
    });
    b.addCase(createTalukThunk.rejected, (s, a) => {
      s.talukSaving = false;
      s.error = a.payload || "Create failed";
    });

    b.addCase(updateTalukThunk.pending, (s) => {
      s.talukSaving = true;
      s.error = null;
    });
    b.addCase(updateTalukThunk.fulfilled, (s) => {
      s.talukSaving = false;
    });
    b.addCase(updateTalukThunk.rejected, (s, a) => {
      s.talukSaving = false;
      s.error = a.payload || "Update failed";
    });

    b.addCase(deleteTalukThunk.pending, (s) => {
      s.talukDeleting = true;
      s.error = null;
    });
    b.addCase(deleteTalukThunk.fulfilled, (s) => {
      s.talukDeleting = false;
    });
    b.addCase(deleteTalukThunk.rejected, (s, a) => {
      s.talukDeleting = false;
      s.error = a.payload || "Delete failed";
    });
  },
});

export const { openDtModal, closeDtModal, setDtForm, resetDtForm, setTalukFilters } =
  slice.actions;

export default slice.reducer;
